// frontend/src/components/asset/PendingReturnsShared.js
/**
 * Shared building blocks for the "Pending Returns" workflow.
 *
 * Both portals consume these components so that any change to the custodian's
 * acknowledge-receipt experience flows to both surfaces simultaneously:
 *
 *   - frontend/src/pages/admin/AssetInventory.js        (HR Portal — Pending Returns tab)
 *   - frontend/src/pages/asset-admin/PendingReturns.js  (Asset Custodian Portal — full page)
 *
 * Both portals are admin-themed (red), so the modals here are styled with the
 * admin design tokens (input-admin / select-admin / modal-close-admin /
 * btn-admin-* / btn-success / btn-danger). Do NOT add a theme prop unless a
 * non-admin caller is added.
 *
 * Public exports:
 *   - RETURN_REASON_LABELS (legacy label map — prefer StatusBadge + statusConfig)
 *   - PendingReturnCategoryIcon
 *   - PendingReturnCard
 *   - ConfirmReturnModal   (custodian acknowledges receipt; supports photo upload)
 *   - RejectReturnModal    (custodian rejects employee's return request)
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, Calendar, Camera, CheckCircle, Clock, CreditCard,
  DoorOpen, FileKey, Fuel, ImagePlus, Laptop, MapPin, Package, Smartphone,
  ShieldOff, Upload, User, X, XCircle, Car,
} from 'lucide-react';
import toast from '../../utils/toast';
import { formatDate } from '../../utils/dateUtils';
import {
  isSoftwareLicenseAsset,
  requiresPhysicalHandover,
  usesAccessDeactivationWorkflow,
} from '../../utils/assetAccess';
import Modal from '../UI/Modal';
import FormSelect from '../UI/FormSelect';
import { StatusBadge } from '../UI/StatusBadge';

// ============================================
// PHOTO UPLOAD LIMITS
// ============================================
export const RETURN_PHOTO_MAX = 5;
export const RETURN_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const RETURN_PHOTOS_TOTAL_BYTES = 25 * 1024 * 1024;
export const RETURN_ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Pre-submit validation for return / accident photos (size + total).
 * Mirrors backend limits so the user gets instant feedback before upload.
 *
 * @param {File[]} photos
 * @param {{ maxPhotos?: number, maxBytes?: number, totalBytes?: number }} [limits]
 * @returns {string|null} error message, or null if valid
 */
export const validateReturnPhotosForSubmit = (photos, limits = {}) => {
  const maxPhotos = limits.maxPhotos ?? RETURN_PHOTO_MAX;
  const maxBytes = limits.maxBytes ?? RETURN_PHOTO_MAX_BYTES;
  const totalBytes = limits.totalBytes ?? RETURN_PHOTOS_TOTAL_BYTES;
  if (!photos || photos.length === 0) return null;
  if (photos.length > maxPhotos) return `Maximum ${maxPhotos} photos.`;
  if (photos.some((f) => f.size > maxBytes)) {
    return `Each photo must be ${Math.round(maxBytes / (1024 * 1024))}MB or less.`;
  }
  const total = photos.reduce((s, f) => s + f.size, 0);
  if (total > totalBytes) {
    return `Total size of photos must be ${Math.round(totalBytes / (1024 * 1024))}MB or less.`;
  }
  return null;
};

// ============================================
// RETURN REASONS
// ============================================
export const RETURN_REASON_LABELS = {
  no_longer_needed: 'No Longer Needed',
  faulty: 'Faulty/Not Working',
  damaged: 'Damaged',
  upgrade_requested: 'Upgrade Requested',
  other: 'Other',
};

// ============================================
// CATEGORY ICON
// ============================================
const CATEGORY_ICONS = {
  IT: Laptop,
  ACC: DoorOpen,
  FLT: Car,
  LIC: FileKey,
  OFF: Package,
  FUEL: CreditCard,
  DSL: Fuel,
  TEL: Smartphone,
};

export const PendingReturnCategoryIcon = ({ code, className = 'w-5 h-5' }) => {
  const Icon = CATEGORY_ICONS[code] || Package;
  return <Icon className={className} />;
};

// ============================================
// MOBILE DETECTION HOOK
// ============================================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => {
      const ua = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(ua || window.innerWidth <= 768);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

// ============================================
// RETURN PHOTO UPLOADER (shared)
// ============================================
/**
 * Reusable, parent-controlled photo picker for the asset return / receipt
 * workflows. Encapsulates the mobile (camera vs gallery) and desktop
 * (drag-and-drop) input modes, image-type filtering, the max-count guard, and
 * the thumbnail previews with per-item removal.
 *
 * Used by:
 *  - ConfirmReturnModal              (custodian acknowledges employee return)
 *  - ReturnModal / ReturnAssetModal  (custodian/HR immediate return to inventory)
 *
 * Parent owns the photos array (so it can run validateReturnPhotosForSubmit on
 * submit and include the files in its mutation payload).
 *
 * Props:
 *  - photos: File[]            current selected files
 *  - onChange: (File[]) => void
 *  - label?: string           field label
 *  - helpText?: string        helper line under the input
 *  - maxPhotos?: number       default RETURN_PHOTO_MAX (5)
 *  - maxBytes?: number        per-file byte limit
 *  - totalBytes?: number      combined byte limit
 *  - theme?: 'admin'|'employee'  accent colour (default admin)
 */
export const ReturnPhotoUploader = ({
  photos,
  onChange,
  label = 'Photos (optional)',
  helpText,
  maxPhotos = RETURN_PHOTO_MAX,
  maxBytes = RETURN_PHOTO_MAX_BYTES,
  totalBytes = RETURN_PHOTOS_TOTAL_BYTES,
  theme = 'admin',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useIsMobile();
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const isAdmin = theme === 'admin';
  const ringFocus = isAdmin
    ? 'focus-visible:ring-red-500'
    : 'focus-visible:ring-blue-800';
  const hoverBorder = isAdmin ? 'hover:border-red-400' : 'hover:border-blue-400';
  const iconAccent = isAdmin ? 'text-red-600' : 'text-blue-800';
  const dragActive = isAdmin ? 'border-red-400 bg-red-50' : 'border-blue-400 bg-blue-50';
  const resolvedHelp = helpText
    ?? `Up to ${maxPhotos} images, ${Math.round(maxBytes / (1024 * 1024))}MB each. JPEG, PNG, WebP.`;

  const addFiles = (incoming) => {
    const images = (incoming || []).filter((f) => RETURN_ALLOWED_IMAGE_TYPES.includes(f.type));
    if (incoming?.length && images.length === 0) {
      toast.error('No image files (JPEG, PNG, WebP) found.');
      return;
    }
    const oversize = images.filter((f) => f.size > maxBytes);
    if (oversize.length > 0) {
      toast.error(`Each photo must be ${Math.round(maxBytes / (1024 * 1024))}MB or less.`);
      return;
    }
    const slotsLeft = maxPhotos - photos.length;
    if (slotsLeft <= 0) {
      toast.error(`Maximum ${maxPhotos} photos.`);
      return;
    }
    if (images.length > slotsLeft) {
      toast.error(`Only ${slotsLeft} more photo(s) can be added.`);
    }
    const next = [...photos, ...images].slice(0, maxPhotos);
    const total = next.reduce((s, f) => s + f.size, 0);
    if (total > totalBytes) {
      toast.error(
        `Total size of photos must be ${Math.round(totalBytes / (1024 * 1024))}MB or less.`
      );
      return;
    }
    onChange(next);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setIsDragging(true);
    else if (e.type === 'dragleave') setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer?.files || []));
  };

  const handleFileChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}

      {isMobile ? (
        <div className="grid grid-cols-2 gap-2">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className={`w-full bg-white hover:bg-gray-50 text-gray-700 rounded-lg py-3 px-3 border-2 border-gray-300 ${hoverBorder} hover:shadow-md transition-all flex flex-col items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 ${ringFocus}`}
          >
            <Camera className={`w-6 h-6 ${iconAccent}`} />
            <span className="text-xs font-medium">Take Photo</span>
          </button>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            type="button"
            onClick={() => galleryInputRef.current?.click()}
            className={`w-full bg-white hover:bg-gray-50 text-gray-700 rounded-lg py-3 px-3 border-2 border-gray-300 ${hoverBorder} hover:shadow-md transition-all flex flex-col items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 ${ringFocus}`}
          >
            <Upload className={`w-6 h-6 ${iconAccent}`} />
            <span className="text-xs font-medium">From Gallery</span>
          </button>
        </div>
      ) : (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => galleryInputRef.current?.click()}
          className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition ${
            isDragging ? dragActive : 'border-gray-300 hover:bg-gray-50'
          }`}
        >
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <ImagePlus className="w-8 h-8 text-gray-400 mb-1.5" />
          <p className="text-sm text-gray-600">Drag & drop or click to add photos</p>
        </div>
      )}

      {resolvedHelp && <p className="text-xs text-gray-500 mt-1.5">{resolvedHelp}</p>}

      {photos.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {photos.map((file, i) => {
            const previewUrl = URL.createObjectURL(file);
            return (
              <div key={i} className="relative group">
                <img
                  src={previewUrl}
                  alt={`Preview ${i + 1}`}
                  className="w-full h-24 object-cover rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    URL.revokeObjectURL(previewUrl);
                    onChange(photos.filter((_, idx) => idx !== i));
                  }}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove photo"
                >
                  <X className="w-3 h-3" />
                </button>
                <div className="absolute bottom-1 left-1 right-1 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded truncate">
                  {file.name}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// PENDING RETURN CARD
// ============================================
/**
 * Card showing a single employee-initiated return request awaiting custodian
 * action.
 *
 * Props:
 *  - returnTransfer: AssetTransfer payload (return_reason, asset_tag, asset_type_name,
 *      from_employee, transfer_date, expires_at, notes, asset_detail/asset)
 *  - onConfirm(returnTransfer): open the confirm-receipt modal
 *  - onReject(returnTransfer): open the reject-return modal
 */
export const PendingReturnCard = ({ returnTransfer, onConfirm, onReject }) => {
  const expiresAt = returnTransfer.expires_at ? new Date(returnTransfer.expires_at) : null;
  const isExpiringSoon = expiresAt && (expiresAt - new Date()) < 24 * 60 * 60 * 1000;
  const categoryCode = returnTransfer.asset_detail?.asset_type?.category?.code
    || returnTransfer.asset?.asset_type?.category?.code;

  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
            <PendingReturnCategoryIcon code={categoryCode} className="w-5 h-5 text-orange-600" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-900 truncate">{returnTransfer.asset_tag}</h4>
            <p className="text-sm text-gray-600 truncate">{returnTransfer.asset_type_name}</p>
          </div>
        </div>
        <span className="whitespace-nowrap flex-shrink-0">
          <StatusBadge status={returnTransfer.return_reason} />
        </span>
      </div>

      <div className="space-y-2 flex-1">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <User className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">
            {returnTransfer.from_employee?.first_name} {returnTransfer.from_employee?.last_name}
          </span>
          {returnTransfer.from_employee?.staff_id && (
            <span className="text-gray-400 text-xs">({returnTransfer.from_employee.staff_id})</span>
          )}
        </div>

        {returnTransfer.notes && (
          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2 italic">
            "{returnTransfer.notes}"
          </p>
        )}

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Requested {formatDate(returnTransfer.transfer_date)}
          </span>
          {expiresAt && (
            <span className={`flex items-center gap-1 ${isExpiringSoon ? 'text-red-600 font-medium' : ''}`}>
              <Clock className="w-3 h-3" />
              Expires {formatDate(expiresAt)}
              {isExpiringSoon && ' (Soon!)'}
            </span>
          )}
        </div>
      </div>

      <div className="card-action-footer flex-nowrap btn-row-spread">
        <button
          type="button"
          onClick={() => onReject(returnTransfer)}
          className="btn btn-sm btn-admin-outline text-xs"
        >
          <XCircle className="w-3.5 h-3.5" />
          Reject
        </button>
        <button
          type="button"
          onClick={() => onConfirm(returnTransfer)}
          className="btn btn-sm btn-success text-xs"
        >
          <CheckCircle className="w-3.5 h-3.5" />
          Acknowledge Receipt
        </button>
      </div>
    </div>
  );
};

// ============================================
// CONFIRM RETURN MODAL (with photo upload)
// ============================================
/**
 * Custodian acknowledges receipt of an employee-initiated return.
 *
 * The photo upload supports three input modes that match the custodian's
 * physical workflow:
 *  - Mobile: split "Take Photo" (camera) vs "From Gallery" buttons
 *  - Desktop: drag-and-drop dropzone (also click to open file picker)
 *
 * onConfirm payload:
 *   { location: string, condition: string|undefined, photos: File[]|undefined }
 *
 * The transferId is owned by the parent so this modal stays purely
 * presentational — caller wires it into useConfirmReturn().mutate.
 */
export const ConfirmReturnModal = ({
  isOpen,
  onClose,
  returnTransfer,
  onConfirm,
  isLoading,
}) => {
  const [location, setLocation] = useState('Inventory');
  const [condition, setCondition] = useState('');
  const [photos, setPhotos] = useState([]);
  const isSoftware = isSoftwareLicenseAsset(returnTransfer);
  const isPhysicalHandover = requiresPhysicalHandover(returnTransfer);
  const usesDeactivation = usesAccessDeactivationWorkflow(returnTransfer);

  // Reset on open. Photo previews use object URLs that the uploader revokes
  // when an item is removed; they leak only if the modal is closed mid-flow
  // without removing previews (acceptable — browser GC reclaims).
  useEffect(() => {
    if (isOpen) {
      setLocation('Inventory');
      setCondition('');
      setPhotos([]);
    }
  }, [isOpen]);

  const isFaultyOrDamaged = (
    isPhysicalHandover
    && ['faulty', 'damaged'].includes(returnTransfer?.return_reason)
  );

  const handleSubmit = () => {
    if (usesDeactivation) {
      toast.error('Use Deactivate Access for this digital credential.');
      return;
    }
    if (isPhysicalHandover) {
      const photoError = validateReturnPhotosForSubmit(photos);
      if (photoError) {
        toast.error(photoError);
        return;
      }
    }
    onConfirm({
      location: isPhysicalHandover ? (location.trim() || 'Inventory') : '',
      condition: isPhysicalHandover ? (condition || undefined) : undefined,
      photos: isPhysicalHandover && photos.length > 0 ? photos : undefined,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Acknowledge Return Receipt"
      size="md"
      theme="admin"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={isLoading}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || usesDeactivation}
            className="btn btn-success"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {isLoading ? 'Confirming...' : 'Confirm Receipt'}
          </button>
        </>
      }
    >
            {/* Asset + employee info */}
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-sm font-medium text-green-800">{returnTransfer?.asset_tag}</p>
              <p className="text-xs text-green-600">{returnTransfer?.asset_type_name}</p>
              <p className="text-xs text-green-700 mt-1">
                From: {returnTransfer?.from_employee?.first_name} {returnTransfer?.from_employee?.last_name}
                {returnTransfer?.from_employee?.staff_id && (
                  <span className="text-green-600"> ({returnTransfer.from_employee.staff_id})</span>
                )}
              </p>
            </div>

            {/* Return reason + employee notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Reason</label>
              <StatusBadge status={returnTransfer?.return_reason} />
              {returnTransfer?.notes && (
                <p className="text-sm text-gray-600 mt-2 bg-gray-50 rounded-lg p-2 italic">
                  "{returnTransfer.notes}"
                </p>
              )}
            </div>

            {!isPhysicalHandover && (
              <div className="flex items-start gap-2 rounded-lg bg-purple-50 p-3">
                {usesDeactivation ? (
                  <ShieldOff className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                ) : (
                  <FileKey className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                )}
                <p className="text-sm text-purple-800">
                  {usesDeactivation
                    ? 'Digital access credentials must be deactivated rather than received into inventory.'
                    : `${isSoftware ? 'Software seat' : 'Digital entitlement'} — confirming receipt reclaims the entitlement. Condition, photos, and storage location do not apply.`}
                </p>
              </div>
            )}

            {isFaultyOrDamaged && (
              <div className="bg-yellow-50 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-700">
                  This asset will be marked as "In Repair" and a maintenance log will be created automatically.
                </p>
              </div>
            )}

            {isPhysicalHandover && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirm-return-location">
                    Storage Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      id="confirm-return-location"
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Where is this asset stored?"
                      className="input-admin w-full pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition (optional)</label>
                  <FormSelect
                    value={condition}
                    onChange={setCondition}
                    options={[
                      { value: '', label: 'Keep current condition' },
                      { value: 'new', label: 'New' },
                      { value: 'good', label: 'Good' },
                      { value: 'fair', label: 'Fair' },
                      { value: 'poor', label: 'Poor' },
                      { value: 'faulty', label: 'Faulty' },
                    ]}
                    theme="admin"
                  />
                </div>

                <ReturnPhotoUploader
                  photos={photos}
                  onChange={setPhotos}
                  label="Photos (optional, recommended for faulty/damaged returns)"
                />
              </>
            )}
    </Modal>
  );
};

// ============================================
// REJECT RETURN MODAL
// ============================================
/**
 * Custodian rejects an employee's return request (e.g., asset is fine, no
 * issue justifies the return). The asset stays assigned to the employee.
 *
 * onReject payload:
 *   { reason: string }
 */
export const RejectReturnModal = ({
  isOpen,
  onClose,
  returnTransfer,
  onReject,
  isLoading,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Reject Return Request"
      size="sm"
      theme="admin"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={isLoading}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onReject({ reason })}
            disabled={isLoading || !reason.trim()}
            className="btn btn-danger"
          >
            <XCircle className="w-4 h-4 mr-2" />
            {isLoading ? 'Rejecting...' : 'Reject Return'}
          </button>
        </>
      }
    >
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-sm font-medium text-red-800">{returnTransfer?.asset_tag}</p>
              <p className="text-xs text-red-600">{returnTransfer?.asset_type_name}</p>
              <p className="text-xs text-red-700 mt-1">
                From: {returnTransfer?.from_employee?.first_name} {returnTransfer?.from_employee?.last_name}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee's Reason</label>
              <StatusBadge status={returnTransfer?.return_reason} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please explain why this return is being rejected..."
                className="input-admin w-full h-24 resize-none"
                required
              />
            </div>

            <div className="bg-yellow-50 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-700">
                The employee will be notified that their return request was rejected.
                The asset will remain assigned to them.
              </p>
            </div>
    </Modal>
  );
};
